'use client';
import { Typography } from '@curology/ui-components-web/react/Typography';
import Image from 'next/image';
import React from 'react';

import { centsToDollarsAndCents } from '@/helpers/productSelectionAndCheckout/helpers';
import { type CheckoutItemData, type Money } from '@/types/cartCheckout/types';

interface Props {
  items: CheckoutItemData[];
  tax: Money;
  discount?: Money;
  total: Money;
}

function CartPriceSummary({ items, tax, discount, total }: Props) {
  const itemCount = items.reduce((count, item) => count + item.quantity, 0);

  return (
    <div className="cur-bg-surface-neutral cur-mt-4 cur-p-4" id="price-summary">
      <ul className="cur-mb-0">
        {items.map((item) => (
          <li className="cur-flex cur-justify-between cur-mb-2" key={item.id}>
            <div className="cur-flex cur-items-start">
              {item.thumbnailImage?.url ? (
                <>
                  <Image
                    alt={item.thumbnailImage.alt}
                    className="cur-mr-2"
                    height={50}
                    src={item.thumbnailImage.url}
                    width={40}
                  />
                </>
              ) : null}
              <div>
                <Typography weight="bold">
                  {item.quantity > 1 ? (
                    <span>{item.quantity}x&nbsp;</span>
                  ) : null}
                  {item.name}
                  {item.isRx ? <sup>Rx</sup> : null}
                </Typography>
                <Typography>{item.volume}</Typography>
              </div>
            </div>
            <div className="cur-ml-2 cur-text-right">
              <Typography weight="bold">
                {centsToDollarsAndCents(item.unitPrice * item.quantity)}
              </Typography>
            </div>
          </li>
        ))}
        <hr className="cur-bg-border-disabled cur-border-0 cur-h-[1px] cur-mb-2" />
        <li className="cur-flex cur-justify-between cur-mb-2">
          <Typography>Shipping</Typography>
          <Typography>
            <span className="cur-text-brand cur-font-bold">FREE</span>
            <span className="cur-text-disabled">
              {' '}
              <span className="cur-line-through">$5.00</span>
            </span>
          </Typography>
        </li>
        <li className="cur-flex cur-justify-between cur-mb-2">
          <Typography>Dermatology consultation</Typography>
          <Typography className="cur-pl-4">
            <span className="cur-text-brand cur-font-bold">INCLUDED</span>
          </Typography>
        </li>
        {discount?.amount && discount.amount > 0 && (
          <li className="cur-flex cur-justify-between cur-mb-2">
            <Typography>Discount</Typography>
            <Typography>-{centsToDollarsAndCents(discount.amount)}</Typography>
          </li>
        )}
        {tax?.amount && tax.amount >= 0 && (
          <li className="cur-flex cur-justify-between cur-mb-2">
            <Typography>Estimated Tax</Typography>
            <Typography>{centsToDollarsAndCents(tax.amount)}</Typography>
          </li>
        )}
        <hr className="cur-bg-border-disabled cur-border-0 cur-h-[1px] cur-mb-2" />
        <li className="cur-flex cur-justify-between">
          <Typography>
            Total ({itemCount} Item{itemCount === 1 ? '' : 's'})
          </Typography>
          <Typography weight="bold">
            {centsToDollarsAndCents(total.amount)}
          </Typography>
        </li>
      </ul>
    </div>
  );
}

export default CartPriceSummary;
